import axios from 'axios';
import { getCookie, setCookie, deleteCookie } from '../utils/cookies';

// Tạo instance axios với cấu hình mặc định
const axiosInstance = axios.create({
	baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8888/api',
	timeout: 10000,
	headers: {
		'Content-Type': 'application/json',
	},
});

// Trạng thái đang refresh để tránh gọi nhiều lần song song
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
	failedQueue.forEach(({ resolve, reject }) => {
		if (error) {
			reject(error);
		} else {
			resolve(token);
		}
	});
	failedQueue = [];
};

// Request interceptor - lấy token từ cookie và thêm vào header
axiosInstance.interceptors.request.use(
	(config) => {
		const token = getCookie('token');
		if (token) {
			config.headers.Authorization = `Bearer ${ token }`;
		}
		// Nếu data là FormData, xóa Content-Type để browser tự set đúng boundary
		if (config.data instanceof FormData) {
			delete config.headers['Content-Type'];
		}
		return config;
	},
	(error) => Promise.reject(error)
);

// Response interceptor - tự động refresh token khi hết hạn
axiosInstance.interceptors.response.use(
	(response) => response.data,
	async (error) => {
		const originalRequest = error.config;

		// Nếu lỗi 401 và chưa retry, thử refresh token
		if (error.response?.status === 401 && !originalRequest._retry) {
			// Bỏ qua các endpoint auth (login, register, ...) — lỗi này là lỗi đăng nhập thật sự
			if (originalRequest.url?.includes('/auth/')) {
				return Promise.reject(error);
			}

			const refreshToken = getCookie('refreshToken');

			// Không có refresh token → logout
			if (!refreshToken) {
				localStorage.removeItem('user');
				deleteCookie('token');
				window.location.href = '/login';
				return Promise.reject(error);
			}

			// Đang có request refresh khác → xếp hàng chờ
			if (isRefreshing) {
				return new Promise((resolve, reject) => {
					failedQueue.push({ resolve, reject });
				}).then((newToken) => {
					originalRequest.headers.Authorization = `Bearer ${ newToken }`;
					return axiosInstance(originalRequest);
				}).catch((err) => Promise.reject(err));
			}

			originalRequest._retry = true;
			isRefreshing = true;

			try {
				const res = await axios.post(
					`${ process.env.REACT_APP_API_URL || 'http://localhost:8888/api' }/auth/refresh-token`,
					{ refreshToken }
				);

				const newToken = res.data?.data?.token;
				if (!newToken) throw new Error('Không lấy được token mới');

				// Lưu token mới vào cookie (1 ngày)
				setCookie('token', newToken, 1);

				// Cập nhật header cho request gốc
				originalRequest.headers.Authorization = `Bearer ${ newToken }`;

				// Xử lý hàng đợi
				processQueue(null, newToken);

				// Retry request gốc
				return axiosInstance(originalRequest);
			} catch (refreshError) {
				processQueue(refreshError, null);
				localStorage.removeItem('user');
				deleteCookie('token');
				deleteCookie('refreshToken');
				window.location.href = '/login';
				return Promise.reject(refreshError);
			} finally {
				isRefreshing = false;
			}
		}

		// Xử lý các lỗi khác
		if (error.response) {
			switch (error.response.status) {
				case 403:
					console.error('Bạn không có quyền truy cập');
					break;
				case 404:
					console.error('Không tìm thấy tài nguyên');
					break;
				case 500:
					console.error('Lỗi server');
					break;
				default:
					break;
			}
		} else if (error.request) {
			console.error('Không thể kết nối đến server');
		}

		return Promise.reject(error);
	}
);

export default axiosInstance;
